'use client';

import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as XLSX from 'xlsx';
import { useTranslation } from 'react-i18next';
import { useAIStore } from '../../stores/aiStore';
import { useUploadedFileStore } from '../../stores/uploadedFileStore';
import { useWorkflowStore } from '../../stores/workflowStore';
import { getNodeDisplayLabel } from '../../utils/nodeDisplayLabel';
import {
  Robot,
  X,
  PaperPlaneRight,
  Spinner,
  Lightning,
  CheckCircle,
  ArrowCounterClockwise,
  Upload,
  FileXls,
  Trash,
  ChatTeardropText,
} from '@phosphor-icons/react';

interface AIDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onWorkflowGenerated?: (workflow: string) => void;
}

const EXAMPLE_PROMPT_KEYS = [
  'nodes.aiAssistant.examplePrompts.example1',
  'nodes.aiAssistant.examplePrompts.example2',
  'nodes.aiAssistant.examplePrompts.example3',
  'nodes.aiAssistant.examplePrompts.example4',
  'nodes.aiAssistant.examplePrompts.example5',
];

function inferSemanticType(columnName: string): string {
  const lower = columnName.toLowerCase();
  if (lower.includes('id') || lower.includes('编号')) return 'id';
  if (lower.includes('name') || lower.includes('名称')) return 'name';
  if (lower.includes('amount') || lower.includes('price') || lower.includes('金额') || lower.includes('价格')) return 'money';
  if (lower.includes('date') || lower.includes('time') || lower.includes('日期') || lower.includes('时间')) return 'date';
  if (lower.includes('count') || lower.includes('quantity') || lower.includes('数量')) return 'quantity';
  return '';
}

// 解析 Excel/CSV 文件
async function parseFile(file: File): Promise<{
  name: string;
  rowCount: number;
  columns: string[];
  data: Record<string, unknown>[];
}> {
  const ext = file.name.toLowerCase();
  const isCSV = ext.endsWith('.csv');
  
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result;
        if (!content) {
          reject(new Error('文件内容为空'));
          return;
        }

        let data: Record<string, unknown>[] = [];
        let columns: string[] = [];

        if (isCSV) {
          // 解析 CSV
          const text = content as string;
          const lines = text.split('\n').filter(line => line.trim());
          if (lines.length === 0) {
            reject(new Error('CSV 文件为空'));
            return;
          }
          
          // 解析表头
          columns = lines[0].split(',').map(col => col.trim().replace(/^["']|["']$/g, ''));
          
          // 解析数据行
          for (let i = 1; i < lines.length; i++) {
            const values = lines[i].split(',').map(val => val.trim().replace(/^["']|["']$/g, ''));
            const row: Record<string, unknown> = {};
            columns.forEach((col, idx) => {
              row[col] = values[idx] ?? '';
            });
            data.push(row);
          }
        } else {
          // 解析 Excel
          const workbook = XLSX.read(content, { type: 'binary' });
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1 }) as unknown[][];
          
          if (jsonData.length === 0) {
            reject(new Error('Excel 文件为空'));
            return;
          }
          
          columns = (jsonData[0] as string[]).map(col => String(col));
          
          for (let i = 1; i < jsonData.length; i++) {
            const row: Record<string, unknown> = {};
            columns.forEach((col, idx) => {
              row[col] = jsonData[i][idx] ?? '';
            });
            data.push(row);
          }
        }

        resolve({
          name: file.name,
          rowCount: data.length,
          columns,
          data,
        });
      } catch (error) {
        reject(error instanceof Error ? error : new Error('解析文件失败'));
      }
    };
    reader.onerror = () => reject(new Error('读取文件失败'));
    
    if (isCSV) {
      reader.readAsText(file);
    } else {
      reader.readAsBinaryString(file);
    }
  });
}

export function AIDrawer({ isOpen, onClose, onWorkflowGenerated }: AIDrawerProps) {
  const { t, i18n } = useTranslation();
  const [input, setInput] = useState('');
  const [applied, setApplied] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const {
    parseIntentStream,
    modifyWorkflow,
    isParsing,
    buildSteps,
    currentIntent,
    currentPlan,
    generatedWorkflow,
    applyGeneratedWorkflow,
    clear,
    generatedNodes,
  } = useAIStore();

  const {
    aiDrawerFiles,
    addAiDrawerFile,
    removeAiDrawerFile,
    clearAiDrawerFiles,
  } = useUploadedFileStore();

  const hasFiles = aiDrawerFiles.length > 0;

  // Focus textarea when drawer opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => textareaRef.current?.focus(), 300);
      setApplied(false);
    }
  }, [isOpen]);

  const handleFileSelect = async (files: FileList | null) => {
    if (!files?.length) return;
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const ext = file.name.toLowerCase();
      if (!ext.endsWith('.xlsx') && !ext.endsWith('.xls') && !ext.endsWith('.csv')) continue;
      try {
        const parsed = await parseFile(file);
        addAiDrawerFile({
          id: `file_${Date.now()}_${i}`,
          ...parsed,
        });
      } catch (error) {
        console.error('解析文件失败:', error);
      }
    }
    if (files.length > 0) {
      clear();
      setInput('');
      setApplied(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const handleRemoveFile = (id?: string) => {
    if (id) {
      removeAiDrawerFile(id);
    } else {
      clearAiDrawerFiles();
      const workflowStore = useWorkflowStore.getState();
      workflowStore.clearWorkflow();
      useUploadedFileStore.getState().clearAllFiles();
    }
    clear();
    setInput('');
    setApplied(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isParsing || !hasFiles) return;

    setApplied(false);
    // 多表 context：每个文件对应一张表，表名用文件名（去扩展名）便于与 plan 中 load 步骤对应
    const tableName = (name: string) => name.replace(/\.[^/.]+$/, '') || name;
    const context = {
      availableTables: aiDrawerFiles.map((f) => ({
        name: tableName(f.name),
        columns: f.columns.map((col) => ({
          name: col,
          type: 'string',
          semanticType: inferSemanticType(col),
        })),
      })),
      sampleData: {
        headers: aiDrawerFiles[0].columns,
        rows: (aiDrawerFiles[0].data || []).slice(0, 15),
      },
    };

    if (generatedNodes.length > 0) {
      await modifyWorkflow(input.trim(), context);
    } else {
      await parseIntentStream(input.trim(), context);
    }

    if (generatedWorkflow && onWorkflowGenerated) {
      onWorkflowGenerated(generatedWorkflow);
    }
  };

  const handleApply = () => {
    applyGeneratedWorkflow(aiDrawerFiles);
    setApplied(true);
  };

  const handleReset = () => {
    setInput('');
    setApplied(false);
    clear();
  };

  const handleModifyWorkflow = () => {
    // 允许用户继续输入修改需求
    setInput('');
    textareaRef.current?.focus();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 bg-black/20 backdrop-blur-[2px] z-40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />

          {/* Drawer panel */}
          <motion.div
            className="fixed top-0 right-0 h-full w-[480px] bg-white border-l border-zinc-200 shadow-2xl shadow-zinc-900/20 z-50 flex flex-col"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', stiffness: 300, damping: 35 }}
          >
            {/* Header */}
            <div className="flex items-center gap-3 px-5 py-4 border-b border-zinc-100 bg-gradient-to-r from-amber-50 to-orange-50">
              <div className="w-9 h-9 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                <Robot className="w-5 h-5 text-amber-600" weight="fill" />
              </div>
              <div className="flex-1">
                <h2 className="text-sm font-semibold text-zinc-900">{t('nodes.aiAssistant.title')}</h2>
                <p className="text-xs text-zinc-500">{t('nodes.aiAssistant.subtitle')}</p>
              </div>
              <motion.button
                onClick={onClose}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                className="p-1.5 rounded-lg text-zinc-400 hover:text-zinc-600 hover:bg-white/60 transition-colors"
              >
                <X className="w-4 h-4" />
              </motion.button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto">
              <div className="p-5 space-y-5">
                {/* File Upload Section */}
                {!hasFiles ? (
                  <div className="space-y-3">
                    <p className="text-xs font-medium text-zinc-500">{t('nodes.aiAssistant.step1')}</p>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".xlsx,.xls,.csv"
                      multiple
                      onChange={(e) => { handleFileSelect(e.target.files); e.target.value = ''; }}
                      className="hidden"
                    />
                    <div
                      onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                      onDragLeave={() => setIsDragging(false)}
                      onDrop={handleDrop}
                      onClick={() => fileInputRef.current?.click()}
                      className={`
                        border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-all duration-200
                        ${isDragging ? 'border-amber-400 bg-amber-50' : 'border-zinc-300 hover:border-zinc-400 hover:bg-zinc-50'}
                      `}
                    >
                      <div className="w-14 h-14 rounded-xl bg-amber-100 flex items-center justify-center mx-auto mb-4">
                        <Upload className="w-7 h-7 text-amber-600" />
                      </div>
                      <p className="text-sm font-medium text-zinc-700 mb-1">{t('nodes.aiAssistant.uploadClick')}</p>
                      <p className="text-xs text-zinc-400">{t('nodes.aiAssistant.supportedFormats')}</p>
                      <p className="text-[10px] text-zinc-400 mt-1">{t('nodes.aiAssistant.multiFileHint', '可上传多个 Excel/CSV，用于合并、对比等多表操作')}</p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-medium text-zinc-500">{t('nodes.aiAssistant.uploadedFile')}</p>
                      <div className="flex items-center gap-1">
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept=".xlsx,.xls,.csv"
                          multiple
                          onChange={(e) => { handleFileSelect(e.target.files); e.target.value = ''; }}
                          className="hidden"
                        />
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          className="text-[10px] text-amber-600 hover:text-amber-700 font-medium"
                        >
                          {t('nodes.aiAssistant.addMore', '添加更多')}
                        </button>
                        {aiDrawerFiles.length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleRemoveFile()}
                            className="text-[10px] text-zinc-400 hover:text-red-500"
                          >
                            {t('nodes.aiAssistant.clearAll', '清空')}
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="space-y-2 max-h-44 overflow-y-auto">
                      {aiDrawerFiles.map((file) => (
                        <motion.div
                          key={file.id}
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-xl"
                        >
                          <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                            <FileXls className="w-4 h-4 text-amber-600" weight="fill" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-amber-900 truncate">{file.name}</p>
                            <p className="text-[10px] text-amber-600">
                              {file.rowCount.toLocaleString()} {t('nodes.rows')} · {file.columns.length} {t('nodes.columns')}
                            </p>
                          </div>
                          <motion.button
                            type="button"
                            onClick={() => handleRemoveFile(file.id)}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="p-1.5 text-amber-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                            title={t('nodes.aiAssistant.removeFile')}
                          >
                            <Trash className="w-4 h-4" />
                          </motion.button>
                        </motion.div>
                      ))}
                    </div>

                    {/* 多表时仅展示第一张表的列结构示意 */}
                    <div className="p-3 bg-zinc-50 rounded-xl border border-zinc-200">
                      <p className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider mb-2">
                        {aiDrawerFiles.length > 1
                          ? t('nodes.aiAssistant.columnStructureFirst', '首表列结构（供参考）')
                          : t('nodes.aiAssistant.columnStructure')}
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {aiDrawerFiles[0].columns.map((col) => (
                          <span
                            key={col}
                            className="px-2 py-1 text-[10px] bg-white border border-zinc-200 rounded-md text-zinc-600"
                          >
                            {col}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* AI Input Section - Only show when file(s) uploaded */}
                {hasFiles && (
                  <>
                    <div className="h-px bg-zinc-100" />
                    
                    {/* Input form */}
                    <form onSubmit={handleSubmit} className="space-y-3">
                      <label className="block">
                        <span className="text-xs font-medium text-zinc-500 mb-1.5 block">
                          {generatedNodes.length > 0 ? t('nodes.aiAssistant.modifyPrompt') : t('nodes.aiAssistant.step2')}
                        </span>
                        <textarea
                          ref={textareaRef}
                          value={input}
                          onChange={(e) => setInput(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleSubmit(e as unknown as React.FormEvent);
                          }}
                          placeholder={
                            generatedNodes.length > 0
                              ? t('nodes.aiAssistant.modifyPlaceholder')
                              : t('nodes.aiAssistant.generatePlaceholder')
                          }
                          rows={4}
                          disabled={isParsing}
                          className="w-full text-sm text-zinc-700 bg-zinc-50 border border-zinc-200 rounded-xl px-3.5 py-3 resize-none focus:outline-none focus:ring-2 focus:ring-amber-400/30 focus:border-amber-400 placeholder:text-zinc-400 transition-all disabled:opacity-50"
                        />
                      </label>

                      <div className="flex items-center gap-2">
                        <motion.button
                          type="submit"
                          disabled={!input.trim() || isParsing}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          className="flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-medium bg-zinc-900 text-white rounded-xl hover:bg-zinc-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        >
                          {isParsing ? (
                            <>
                              <Spinner className="w-4 h-4 animate-spin" weight="bold" />
                              {t('nodes.aiAssistant.analyzing')}
                            </>
                          ) : (
                            <>
                              <Lightning className="w-4 h-4" weight="fill" />
                              {generatedNodes.length > 0 ? t('nodes.aiAssistant.modifyWorkflow') : t('nodes.aiAssistant.generateWorkflow')}
                            </>
                          )}
                        </motion.button>

                        {(currentIntent || currentPlan) && (
                          <motion.button
                            type="button"
                            onClick={handleReset}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="p-2.5 rounded-xl border border-zinc-200 text-zinc-500 hover:border-zinc-300 hover:text-zinc-700 transition-colors"
                            title={t('common.reset')}
                          >
                            <ArrowCounterClockwise className="w-4 h-4" />
                          </motion.button>
                        )}
                      </div>

                      <p className="text-[10px] text-zinc-400 text-center">
                        {t('nodes.aiAssistant.shortcutHint')}
                      </p>
                    </form>

                    {/* 构建过程（生成 / 修改表格流时 SSE 流式展示） */}
                    {(isParsing || buildSteps.length > 0) && (
                      <div className="space-y-2">
                        <h4 className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider">
                          {t('nodes.aiAssistant.buildProcess')}
                        </h4>
                        <div className="max-h-36 overflow-y-auto rounded-xl border border-zinc-200 bg-zinc-50/80 p-2 space-y-1">
                          {buildSteps.map((step, idx) => {
                            const params = {
                              step: step.step,
                              total: step.total,
                              ...(step.detail && typeof step.detail === 'object' && !Array.isArray(step.detail)
                                ? (step.detail as Record<string, unknown>)
                                : {}),
                            };
                            const stepLabel = step.messageKey
                              ? t(`nodes.aiAssistant.buildSteps.${step.messageKey}`, params)
                              : step.message;
                            return (
                              <div
                                key={idx}
                                className="flex items-start gap-2 text-[11px] py-1 px-2 rounded-lg border-l-2 border-amber-400 bg-white/80"
                              >
                                <span className="text-zinc-400 shrink-0">{idx + 1}.</span>
                                <span className="text-zinc-600 font-medium">{stepLabel}</span>
                                {step.step != null && step.total != null && (
                                  <span className="text-zinc-400 shrink-0">
                                    {step.step}/{step.total}
                                  </span>
                                )}
                              </div>
                            );
                          })}
                          {isParsing && buildSteps.length > 0 && (
                            <div className="flex items-center gap-2 text-[11px] text-zinc-500 py-1 px-2">
                              <Spinner className="w-3 h-3 animate-spin" weight="bold" />
                              {t('nodes.aiAssistant.analyzing')}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Example prompts - Only show when no workflow generated */}
                    {!currentIntent && !currentPlan && generatedNodes.length === 0 && (
                      <div>
                        <p className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider mb-2">{t('nodes.aiAssistant.examples')}</p>
                        <div className="flex flex-wrap gap-1.5">
                          {EXAMPLE_PROMPT_KEYS.map((key) => (
                            <motion.button
                              key={key}
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                              onClick={() => setInput(t(key))}
                              className="px-2.5 py-1 text-xs text-zinc-600 bg-zinc-50 hover:bg-amber-50 hover:text-amber-700 border border-zinc-200 hover:border-amber-200 rounded-full transition-colors"
                            >
                              {t(key)}
                            </motion.button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Results */}
                    <AnimatePresence>
                      {(currentIntent || currentPlan || generatedNodes.length > 0) && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          className="space-y-3"
                        >
                          <div className="h-px bg-zinc-100" />

                          {/* Workflow Preview */}
                          {generatedNodes.length > 0 && (
                            <div className="space-y-1.5">
                              <div className="flex items-center justify-between">
                                <h4 className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider">{t('nodes.aiAssistant.generatedWorkflow')}</h4>
                                <span className="text-[10px] text-zinc-400">{t('nodes.aiAssistant.nodeCount', { count: generatedNodes.length })}</span>
                              </div>
                              <div className="p-3 bg-zinc-50 rounded-xl border border-zinc-200">
                                <div className="space-y-2">
                                  {generatedNodes.map((node, idx) => (
                                    <div key={node.id} className="flex items-center gap-2">
                                      <div className="w-6 h-6 rounded-md bg-amber-100 flex items-center justify-center flex-shrink-0">
                                        <span className="text-[10px] font-semibold text-amber-600">{idx + 1}</span>
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <p className="text-xs font-medium text-zinc-700 truncate">
                                          {getNodeDisplayLabel(node.type, node.data?.label, t, i18n)}
                                        </p>
                                        <p className="text-[10px] text-zinc-400">{node.type}</p>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          )}

                          {/* {currentIntent && (
                            <div className="space-y-1.5">
                              <h4 className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider">{t('nodes.aiAssistant.recognizedIntent')}</h4>
                              <div className="p-3 bg-zinc-50 rounded-xl border border-zinc-100">
                                <div className="flex items-center gap-2 mb-1.5">
                                  <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-[10px] font-semibold rounded-full">
                                    {t(`nodes.nodeTypes.${currentIntent.type}.label`, { defaultValue: currentIntent.type })}
                                  </span>
                                </div>
                                <p className="text-xs text-zinc-700 leading-relaxed">{currentIntent.goal}</p>
                              </div>
                            </div>
                          )} */}

                          {/* {currentPlan && (
                            <div className="space-y-1.5">
                              <h4 className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider">{t('nodes.aiAssistant.executionPlan')}</h4>
                              <div className="p-3 bg-emerald-50 rounded-xl border border-emerald-100">
                                <pre className="text-[11px] text-emerald-800 whitespace-pre-wrap font-mono leading-relaxed">
                                  {currentPlan.description}
                                </pre>
                              </div>
                            </div>
                          )} */}

                          {/* Action buttons */}
                          <div className="flex items-center gap-2">
                            <motion.button
                              onClick={handleApply}
                              disabled={applied}
                              whileHover={{ scale: applied ? 1 : 1.02 }}
                              whileTap={{ scale: applied ? 1 : 0.98 }}
                              className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-semibold rounded-xl transition-all ${
                                applied
                                  ? 'bg-emerald-500 text-white cursor-default'
                                  : 'bg-amber-500 hover:bg-amber-600 text-white'
                              }`}
                            >
                              {applied ? (
                                <>
                                  <CheckCircle className="w-4 h-4" weight="fill" />
                                  {t('nodes.aiAssistant.applied')}
                                </>
                              ) : (
                                <>
                                  <PaperPlaneRight className="w-4 h-4" weight="fill" />
                                  {t('nodes.aiAssistant.applyToCanvas')}
                                </>
                              )}
                            </motion.button>

                            {!applied && (
                              <motion.button
                                onClick={handleModifyWorkflow}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                className="p-3 rounded-xl border border-zinc-200 text-zinc-500 hover:border-amber-300 hover:text-amber-600 transition-colors"
                                title={t('nodes.aiAssistant.continueModify')}
                              >
                                <ChatTeardropText className="w-4 h-4" />
                              </motion.button>
                            )}
                          </div>

                          {!applied && (
                            <p className="text-[10px] text-zinc-400 text-center">
                              {t('nodes.aiAssistant.notSatisfied')}
                            </p>
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="px-5 py-3 border-t border-zinc-100 bg-zinc-50/50">
              <p className="text-[10px] text-zinc-400 text-center">
                {t('nodes.aiAssistant.disclaimer')}
              </p>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
